import { Check, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { updateGoal } from "wasp/client/operations";
import useTranslation from "../../../client/hooks/useTranslation";
import { Button } from "../../../client/components/ui/button";
import { toast } from "../../../client/hooks/use-toast";
import type { GoalWithHistory } from "../../../trading-goals/operations";
import { useLiteData } from "../../data/LiteDataProvider";
import { goalProgress, recentPeriods } from "../../lib/discipline";
import { Bar, Card, Empty } from "../../ui/ui";

type GoalType = "pnl" | "win_rate" | "trades_count" | "custom";
type Period = "daily" | "weekly" | "monthly";

/** Objectifs en cours : progression par goal, pastilles des dernières périodes, terminés repliés. */
export function GoalsCard({ active, completed, onAdd, onEdit }: {
  active: GoalWithHistory[];
  completed: GoalWithHistory[];
  onAdd: () => void;
  onEdit: (g: GoalWithHistory) => void;
}) {
  const t = useTranslation().lite.discipline;
  const tt = useTranslation();
  const { fmt } = useLiteData();
  const [showCompleted, setShowCompleted] = useState(false);

  // Paire courant / cible : l'unité se répète des deux côtés (pnl, win rate), sauf
  // trades_count où elle ne s'affiche qu'une fois ("3 / 10 trades", pas "3 trades / 10 trades").
  const pair = (g: GoalWithHistory) => {
    if (g.type === "pnl") return `${fmt.money(g.currentValue, { sign: false })} / ${fmt.money(g.targetValue, { sign: false })}`;
    if (g.type === "win_rate") return `${fmt.pct(g.currentValue / 100)} / ${fmt.pct(g.targetValue / 100)}`;
    return `${Math.round(g.currentValue)} / ${t.goals.trades(Math.round(g.targetValue))}`;
  };

  // Seuls les goals libres se cochent à la main (le serveur refuse les autres).
  const markDone = async (g: GoalWithHistory, done: boolean) => {
    try {
      await updateGoal({ id: g.id, isCompleted: done });
    } catch (e) {
      console.error(e);
      toast({ title: tt.common.error, variant: "destructive" });
    }
  };

  const addBtn = <Button size="sm" variant="outline" onClick={onAdd}><Plus className="h-4 w-4 mr-1" />{t.goals.add}</Button>;

  if (!active.length && !completed.length) {
    return (
      <Card title={t.goals.title} action={addBtn}>
        <Empty title={t.goals.emptyTitle} action={<Button onClick={onAdd}><Plus className="h-4 w-4 mr-1.5" />{t.goals.emptyCta}</Button>}>{t.goals.emptyText}</Empty>
      </Card>
    );
  }

  const row = (g: GoalWithHistory, muted = false) => {
    const p = goalProgress(g);
    const periods = g.recurrence !== "none" ? recentPeriods(g) : [];
    return (
      <li key={g.id} className={`py-3 first:pt-0 last:pb-0 ${muted ? "opacity-60" : ""}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{g.title}</div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{t.goals.type[g.type as GoalType]}</span>
              {g.recurrence !== "none" && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{t.goals.period[g.recurrence as Period]}</span>}
              {p.remainingDays !== null && !muted && <span className="text-[11px] text-muted-foreground">{t.goals.remaining(p.remainingDays)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {g.type === "custom" && (
              <button type="button" onClick={() => markDone(g, !g.isCompleted)} className={`w-7 h-7 grid place-items-center rounded-full border ${g.isCompleted ? "bg-pnlPositive border-pnlPositive text-background" : "border-border hover:bg-muted"}`} aria-label={t.goals.markDone} aria-pressed={g.isCompleted}>
                {g.isCompleted && <Check size={13} />}
              </button>
            )}
            <button type="button" onClick={() => onEdit(g)} className="w-7 h-7 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground" aria-label={t.goals.edit}><Pencil size={13} /></button>
          </div>
        </div>
        {g.type !== "custom" && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className={p.achieved ? "text-pnlPositive inline-flex items-center gap-1" : "text-muted-foreground"}>{p.achieved && <Check size={12} />}{p.achieved ? t.goals.achieved : ""}</span>
              <span className="font-mono tabular-nums">{pair(g)}</span>
            </div>
            <Bar value={p.ratio} tone={p.achieved ? "gain" : "brand"} />
          </div>
        )}
        {periods.length > 0 && (
          <div className="flex items-center gap-1 mt-2" aria-label={t.goals.recentPeriods}>
            {periods.map((ok, i) => <span key={i} className={`w-2 h-2 rounded-full ${ok ? "bg-pnlPositive" : "bg-pnlNegative/60"}`} />)}
          </div>
        )}
      </li>
    );
  };

  return (
    <Card title={t.goals.title} subtitle={t.goals.inProgress(active.length)} action={addBtn}>
      <ul className="divide-y divide-border">{active.map(g => row(g))}</ul>
      {completed.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <button type="button" onClick={() => setShowCompleted(v => !v)} className="text-xs text-muted-foreground hover:text-foreground">
            {t.goals.completed(completed.length)} · {showCompleted ? t.goals.hideCompleted : t.goals.showCompleted}
          </button>
          {showCompleted && <ul className="divide-y divide-border mt-3">{completed.map(g => row(g, true))}</ul>}
        </div>
      )}
    </Card>
  );
}
